/**
 * JWT Type Detector
 * 
 * Detects JSON Web Tokens (three Base64 segments separated by dots).
 * Higher priority than Base64 since JWTs are technically Base64-encoded.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const JwtDetector: ITypeDetector = {
  id: 'jwt-detector',
  name: 'JWT Detector',
  priority: 100, // Higher than Base64
  detectedType: SEMANTIC_TYPES.JWT,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    // JWT format: header.payload.signature
    const parts = trimmed.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Each part should be valid Base64URL
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
    
    // All three parts must be non-empty and valid Base64URL
    for (const part of parts) {
      if (!part || !base64UrlRegex.test(part)) {
        return null;
      }
    }

    // Try to decode the header to verify it's valid JSON
    try {
      const header = parts[0];
      // Convert Base64URL to Base64
      const base64 = header.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(base64);
      const headerObj = JSON.parse(decoded);
      
      // JWT headers typically have 'alg' and often 'typ'
      if (typeof headerObj === 'object' && headerObj !== null) {
        return SEMANTIC_TYPES.JWT;
      }
    } catch {
      // Not a valid JWT
    }

    return null;
  },
};
